import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  Cookie, Settings, BarChart, Target, ExternalLink,
  Phone, Mail, ArrowRight, Check, X, Building2
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import SEO from '@/components/SEO';

const CookiesPage = () => {
  const { t } = useTranslation();

  const breadcrumbs = [
    { name: "Home", path: "/" },
    { name: "Cookie Policy", path: "/cookies" }
  ];

  const cookieTypes = ['necessary', 'functional', 'analytics', 'marketing'];

  return (
    <div className="min-h-screen bg-offwhite" data-testid="cookies-page">
      <SEO 
        titleKey="seo.cookies.title"
        descriptionKey="seo.cookies.description"
        path="/cookies"
        breadcrumbs={breadcrumbs}
      />
      {/* Hero */}
      <section className="relative pt-32 pb-16 bg-navy-900">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-gold/20 rounded-xl flex items-center justify-center">
                <Cookie className="w-6 h-6 text-gold" />
              </div>
              <span className="text-gold font-semibold uppercase tracking-wider text-sm">
                {t('legal.badge')}
              </span>
            </div>
            <h1 className="font-space text-4xl md:text-5xl font-bold text-white mb-4">
              {t('legal.cookies.title')}
            </h1>
            <p className="text-platinum-light text-lg">
              {t('legal.cookies.subtitle')}
            </p>
            <p className="text-platinum-light/70 text-sm mt-4">
              {t('legal.cookies.lastUpdated')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-12">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            
            {/* What Are Cookies */}
            <Card className="mb-6 border border-platinum-light">
              <CardContent className="p-8">
                <div className="flex items-center gap-3 mb-4">
                  <Cookie className="w-5 h-5 text-gold" />
                  <h2 className="font-space text-xl font-bold text-navy-900">{t('legal.cookies.sections.whatAreCookies.title')}</h2>
                </div>
                <p className="text-slate-700 text-sm mb-4">{t('legal.cookies.sections.whatAreCookies.intro')}</p>
                <p className="text-slate-700 text-sm mb-2">{t('legal.cookies.sections.whatAreCookies.usageIntro')}</p>
                <ul className="space-y-2 text-sm text-slate-700">
                  {(t('legal.cookies.sections.whatAreCookies.usages', { returnObjects: true }) || []).map((usage) => (
                    <li key={`usage-${usage.slice(0, 20)}`} className="flex items-start gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-gold mt-2" />
                      <span>{usage}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            {/* Cookie Types */}
            <Card className="mb-6 border border-platinum-light">
              <CardContent className="p-8">
                <div className="flex items-center gap-3 mb-4">
                  <Settings className="w-5 h-5 text-gold" />
                  <h2 className="font-space text-xl font-bold text-navy-900">{t('legal.cookies.sections.cookieTypes.title')}</h2>
                </div>
                <div className="space-y-4">
                  {cookieTypes.map((type) => (
                    <div key={type} className={`p-4 rounded-lg ${type === 'necessary' ? 'bg-green-50 border border-green-200' : 'bg-slate-50 border border-slate-200'}`}>
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="font-semibold text-navy-900">{t(`legal.cookies.sections.cookieTypes.types.${type}.name`)}</h3>
                        <span className={`px-2 py-1 rounded text-xs ${type === 'necessary' ? 'bg-green-100 text-green-700' : 'bg-slate-200 text-slate-600'}`}>
                          {type === 'necessary' ? t('legal.cookies.sections.cookieTypes.required') : t('legal.cookies.sections.cookieTypes.optional')}
                        </span>
                      </div>
                      <p className="text-slate-600 text-sm">{t(`legal.cookies.sections.cookieTypes.types.${type}.description`)}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Third Party Cookies */}
            <Card className="mb-6 border border-platinum-light">
              <CardContent className="p-8">
                <div className="flex items-center gap-3 mb-4">
                  <BarChart className="w-5 h-5 text-gold" />
                  <h2 className="font-space text-xl font-bold text-navy-900">{t('legal.cookies.sections.thirdParty.title')}</h2>
                </div>
                <p className="text-slate-700 text-sm mb-4">{t('legal.cookies.sections.thirdParty.intro')}</p>
                <div className="space-y-3">
                  {['googleAnalytics', 'cloudflare', 'chat'].map((service) => (
                    <div key={service} className="bg-slate-50 p-4 rounded-lg">
                      <h3 className="font-semibold text-navy-900 mb-1">{t(`legal.cookies.sections.thirdParty.services.${service}.name`)}</h3>
                      <p className="text-slate-600 text-sm">{t(`legal.cookies.sections.thirdParty.services.${service}.description`)}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Managing Preferences */}
            <Card className="mb-6 border border-platinum-light">
              <CardContent className="p-8">
                <div className="flex items-center gap-3 mb-4">
                  <Target className="w-5 h-5 text-gold" />
                  <h2 className="font-space text-xl font-bold text-navy-900">{t('legal.cookies.sections.managing.title')}</h2>
                </div>
                <p className="text-slate-700 text-sm mb-4">{t('legal.cookies.sections.managing.intro')}</p>
                <ul className="space-y-2 text-sm text-slate-700 mb-4">
                  {(t('legal.cookies.sections.managing.options', { returnObjects: true }) || []).map((option) => (
                    <li key={`option-${option.slice(0, 20)}`} className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                      <span>{option}</span>
                    </li>
                  ))}
                </ul>
                <div className="bg-gold/10 p-4 rounded-lg">
                  <p className="text-slate-700 text-sm">{t('legal.cookies.sections.managing.note')}</p>
                </div>
              </CardContent>
            </Card>

            {/* Browser Settings */}
            <Card className="mb-6 border border-platinum-light">
              <CardContent className="p-8">
                <div className="flex items-center gap-3 mb-4">
                  <ExternalLink className="w-5 h-5 text-gold" />
                  <h2 className="font-space text-xl font-bold text-navy-900">{t('legal.cookies.sections.browserSettings.title')}</h2>
                </div>
                <p className="text-slate-700 text-sm mb-4">{t('legal.cookies.sections.browserSettings.intro')}</p>
                <div className="grid md:grid-cols-2 gap-3">
                  {['chrome', 'firefox', 'safari', 'edge'].map((browser) => (
                    <a 
                      key={browser}
                      href={browser === 'chrome' ? 'https://support.google.com/chrome/answer/95647' :
                            browser === 'firefox' ? 'https://support.mozilla.org/en-US/kb/cookies' :
                            browser === 'safari' ? 'https://support.apple.com/guide/safari/manage-cookies' :
                            'https://support.microsoft.com/en-us/microsoft-edge/delete-cookies'}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                    >
                      <ExternalLink className="w-4 h-4 text-gold" />
                      <span className="text-navy-900 text-sm">{t(`legal.cookies.sections.browserSettings.browsers.${browser}`)}</span>
                    </a>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Changes */}
            <Card className="mb-6 border border-platinum-light">
              <CardContent className="p-8">
                <div className="flex items-center gap-3 mb-4">
                  <Settings className="w-5 h-5 text-gold" />
                  <h2 className="font-space text-xl font-bold text-navy-900">{t('legal.cookies.sections.changes.title')}</h2>
                </div>
                <div className="text-slate-700 text-sm space-y-4">
                  <p>{t('legal.cookies.sections.changes.content1')}</p>
                  <p>{t('legal.cookies.sections.changes.content2')}</p>
                </div>
              </CardContent>
            </Card>

            {/* Contact */}
            <Card className="mb-6 border border-platinum-light bg-navy-900">
              <CardContent className="p-8">
                <div className="flex items-center gap-3 mb-4">
                  <Mail className="w-5 h-5 text-gold" />
                  <h2 className="font-space text-xl font-bold text-white">{t('legal.cookies.sections.contact.title')}</h2>
                </div>
                <p className="text-platinum-light text-sm mb-4">{t('legal.cookies.sections.contact.intro')}</p>
                <div className="bg-white/10 p-4 rounded-lg">
                  <p className="text-white font-semibold mb-2">{t('legal.companyName')}</p>
                  <p className="text-platinum-light text-sm">
                    <a href="mailto:info@elementarycargo.com" className="hover:text-gold">info@elementarycargo.com</a>
                  </p>
                  <p className="text-platinum-light text-sm">
                    <a href="tel:+31625381824" className="hover:text-gold">+31 62 538 1824</a>
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Other Legal Documents */}
            <Card className="border border-platinum-light">
              <CardContent className="p-8">
                <h3 className="font-space text-lg font-bold text-navy-900 mb-4">Related Documents</h3>
                <div className="flex flex-wrap gap-4">
                  <Link to="/privacy">
                    <Button variant="outline" className="border-2 border-navy-900/20">
                      {t('legal.viewPrivacy')} <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </Link>
                  <Link to="/terms">
                    <Button variant="outline" className="border-2 border-navy-900/20">
                      {t('legal.viewTerms')} <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CookiesPage;
